import { memo } from "react";

import {
  Box,
  IconButton,
  Select,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { TrashIcon } from "@braintrust/braintrust-ui-components/Icons";
import type { InterviewQuestion } from "@js/types/jobs";

import {
  inputFieldStyles,
  InterviewStyledTextField,
} from "../interview-styled-textfield";

type InterviewQuestionItemProps = {
  item: InterviewQuestion;
  onChange: (id: number, newItem: Partial<InterviewQuestion>) => void;
  onDelete: (id: number) => void;
  index: number;
  displayError?: boolean;
  error?: Record<string, string[]>;
  isDragging: boolean;
};

const FOLLOW_UP_STRATEGY_OPTIONS: Array<{
  value: EnumType<typeof ENUMS.JobInterviewQuestionsFollowUpStrategy>;
  label: string;
}> = [
  {
    value: ENUMS.JobInterviewQuestionsFollowUpStrategy.THOROUGH,
    label: "Many follow-up questions",
  },
  {
    value: ENUMS.JobInterviewQuestionsFollowUpStrategy.NATURAL,
    label: "Some follow-up questions",
  },
  {
    value: ENUMS.JobInterviewQuestionsFollowUpStrategy.YES_OR_NO,
    label: "Yes/No question",
  },
  {
    value: ENUMS.JobInterviewQuestionsFollowUpStrategy.NO_FOLLOW_UP,
    label: "No follow-up question",
  },
];

export const InterviewQuestionItem = memo(
  ({
    displayError,
    error,
    index,
    onChange,
    onDelete,
    item,
    isDragging,
  }: InterviewQuestionItemProps) => {
    const questionError = displayError ? error?.question?.[0] : undefined;
    const followUpStrategyError = displayError
      ? error?.follow_up_strategy?.[0]
      : undefined;
    const isErrorInItem = !!error && !!Object.keys(error).length;

    const defaultBorderColor = isDragging
      ? "var(--dark-violet)"
      : "var(--medium-violet)";
    const borderColor =
      displayError && isErrorInItem ? "var(--negative-2)" : defaultBorderColor;

    const backgroundColor = isDragging
      ? "var(--medium-violet)"
      : "var(--soft-violet)";

    return (
      <Box
        sx={{
          borderRadius: 2,
          backgroundColor,
          border: `1px solid ${borderColor}`,
        }}
        className="transition"
        data-testid="interview-question"
      >
        <Stack
          sx={{
            pr: 2,
            pl: 3,
            py: 3,
          }}
        >
          <Stack
            direction="row"
            sx={{ gap: 2, justifyContent: "space-between", mb: 1 }}
          >
            <Typography
              component="label"
              variant="paragraph"
              size="medium"
              fontWeight={500}
              htmlFor={`question-${index}`}
            >
              Question {index + 1}
            </Typography>
            <Box sx={{ position: "relative" }}>
              <IconButton
                variant="transparent"
                onClick={() => onDelete(item.id)}
                size="x-small"
                aria-label="Remove question"
                sx={{
                  position: "absolute",
                  right: "-4px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <TrashIcon />
              </IconButton>
            </Box>
          </Stack>
          <InterviewStyledTextField
            multiline
            maxRows={4}
            id={`question-${index}`}
            value={item.question}
            placeholder="What would you like to ask?"
            onChange={(ev) => {
              onChange(item.id, { question: ev.target.value });
            }}
            sx={{
              mb: 1,
            }}
            error={!!questionError}
            helperText={questionError}
          />
          <Select
            menuVariant="violet"
            noLabel
            value={
              item.follow_up_strategy ??
              ENUMS.JobInterviewQuestionsFollowUpStrategy.NATURAL
            }
            options={FOLLOW_UP_STRATEGY_OPTIONS}
            onChange={(ev) => {
              const newValue = ev.target.value as EnumType<
                typeof ENUMS.JobInterviewQuestionsFollowUpStrategy
              >;
              onChange(item.id, { follow_up_strategy: newValue });
            }}
            MenuProps={{
              slotProps: {
                paper: {
                  sx: {
                    pb: 0.5,
                    border: "1px solid var(--grey-5)",
                    borderRadius: "8px",
                    "& .MuiMenuItem-root": {
                      padding: "6px 8px",
                      mb: 0.5,
                    },
                  },
                },
              },
            }}
            sx={{
              ...inputFieldStyles,
              alignSelf: "start",
              background: "var(--white)",
              "& .MuiButtonBase-root": {
                mr: "12px",
              },
              "& .MuiInputBase-input": {
                padding: "8px 40px 8px 16px !important",
              },
            }}
            FormControlProps={{
              sx: {
                ".MuiFormHelperText-root": {
                  margin: "8px 0 0 0 !important",
                  fontSize: "13px !important",
                },
              },
            }}
            error={followUpStrategyError}
            helperText={followUpStrategyError}
          />
          <></>
        </Stack>
      </Box>
    );
  },
);
